import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiAxiosInstance from '../../../utils/axiosInstance';
import aiAxiosInstance from '../../../utils/aiAxiosInstance';
import { useTeacher } from '../../../context/TeacherContext';
import { useTeacherStandard } from '../../../hooks/useTeacherStandard';
import { useFilterStandardsByTeaching } from '../../../hooks/useFilterStandardsByTeaching';
import { extractTeachingStandardId } from '../../../utils/mongodb';
import { buildAiAuthHeaders, consumeSSEStream } from '../../../hooks/useSSEStream';
import html2pdf from 'html2pdf.js';
import htmlDocx from 'html-docx-js/dist/html-docx';
import {
  type Conversation,
  type Attachment,
  type Message,
  type CodeProps,
  parseAsciiTable,
  subjects,
  getPersonalizationText,
  UNIVERSAL_TOOLS,
  getSuggestionPriority,
  getSuggestionIcon,
} from '../teachingChatUtils';

export function useChat() {
  const { teacher } = useTeacher();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [currentStream, setCurrentStream] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null); // legacy sentinel (unused for scroll)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRaf = useRef<number | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const autoScrollOncePending = useRef<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);
  // Track the conversation ID being created to prevent race conditions with useEffect
  // Using ID instead of boolean flag prevents issues if effect runs after async operations complete
  const creatingConversationIdRef = useRef<string | null>(null);

  // Compose area attachments and image preview modal state
  const [composeAttachments, setComposeAttachments] = useState<Attachment[]>([]);
  const [preview, setPreview] = useState<{ open: boolean; src: string; alt: string }>({ open: false, src: '', alt: '' });
  const [isDragOver, setIsDragOver] = useState(false);

  const [mode, setModeState] = useState<'guided' | 'free'>('guided');
  const [gradeLevel, setGradeLevel] = useState<string>('');
  const [subject, setSubject] = useState<string>(''); // Will be set from teacher.primarySubject
  const [availableGradeLevels, setAvailableGradeLevels] = useState<string[]>([]);
  const [loadingGradeLevels, setLoadingGradeLevels] = useState(false);
  const [standardsMenuOpen, setStandardsMenuOpen] = useState(false);
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  // Removed advanced toggles (clarify first, scaffolding, EL supports, citations)
  const [standards, setStandards] = useState<{ _id: string; code?: string; standard?: string; description?: string; short_description?: string; strand?: string }[]>([]);
  const [loadingStandards, setLoadingStandards] = useState(false);
  const [standardsSkip, setStandardsSkip] = useState(0);
  const [standardsHasMore, setStandardsHasMore] = useState(true);
  const [convQuery, setConvQuery] = useState('');
  const [standardsQuery, setStandardsQuery] = useState('');
  
  // Strand filter state - initialize to 'Reading' if available, otherwise first available strand
  const [selectedStrand, setSelectedStrand] = useState<string>('Reading');

  // Conversation actions menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuConvId, setMenuConvId] = useState<string | null>(null);
  const openConvMenu = (evt: React.MouseEvent<HTMLElement>, id: string) => {
    evt.stopPropagation();
    setMenuAnchorEl(evt.currentTarget);
    setMenuConvId(id);
  };
  const closeConvMenu = () => { setMenuAnchorEl(null); setMenuConvId(null); };
  const archiveConversation = async (id: string, archived: boolean) => {
    try {
      const res = await apiAxiosInstance.patch(`/api/teaching-chat/conversations/${id}`, { archived });
      // Remove from list if archived; update if unarchived (not shown in list anyway)
      if (archived) setConversations(prev => prev.filter(c => c._id !== id));
      else setConversations(prev => prev.map(c => c._id === id ? res.data : c));
      await refreshConversations();
    } catch (e) {
      console.error(e);
      setActionError('Could not update the conversation. Please try again.');
    }
  };

  // Centralized refresh to ensure list reflects server state
  const refreshConversations = useCallback(async () => {
    try {
      const res = await apiAxiosInstance.get('/api/teaching-chat/conversations');
      setConversations(res.data || []);
      setActionError(null);
    } catch (e) {
      console.error(e);
      setActionError('Could not load conversations. Please refresh the page.');
    }
  }, [activeConversationId]);

  // Refs to assistant message content containers for print/download
  const assistantContentRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const printableCss = `
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #111827; }
    .markdown-body { line-height: 1.7; font-size: 14px; }
    
    /* Aggressive heading protection */
    .markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4 { 
      font-weight: 700; 
      margin: 1.2em 0 0.6em; 
      page-break-after: avoid !important; 
      break-after: avoid-page !important;
      page-break-inside: avoid !important;
      break-inside: avoid-page !important;
    }
    
    /* Keep content immediately after headings together */
    .markdown-body h1 + *, .markdown-body h2 + *, .markdown-body h3 + *, .markdown-body h4 + * {
      page-break-before: avoid !important;
      break-before: avoid-page !important;
    }
    
    /* Protect divs with inline page-break styles (from AI) */
    .markdown-body div[style*="page-break"] {
      page-break-inside: avoid !important;
      break-inside: avoid-page !important;
      display: block !important;
    }
    
    .markdown-body p { margin: 0 0 0.9em; }
    .markdown-body ul, .markdown-body ol { 
      margin: 0.6em 0 0.9em 1.2em;
      page-break-inside: auto;
    }
    .markdown-body li { 
      margin: 0.25em 0; 
      page-break-inside: avoid; 
      break-inside: avoid-page; 
    }
    .markdown-body hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.2em 0; page-break-inside: avoid; break-inside: avoid-page; }
    .markdown-body blockquote { border-left: 4px solid #e5e7eb; padding-left: 12px; margin: 0.9em 0; color: #374151; }
    .markdown-body table { border-collapse: collapse; width: 100%; margin: 1em 0; page-break-inside: avoid; break-inside: avoid-page; }
    .markdown-body th, .markdown-body td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
    .markdown-body tr { page-break-inside: avoid; break-inside: avoid-page; }
    .markdown-body pre { background: #0b1020; color: #e5e7eb; padding: 12px; border-radius: 6px; overflow: auto; page-break-inside: avoid; break-inside: avoid-page; }
    .markdown-body code { background: #f3f4f6; padding: 2px 4px; border-radius: 4px; }
    
    @media print {
      /* Extra protection for print */
      .markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4 { 
        page-break-after: avoid !important;
        break-after: avoid-page !important;
        orphans: 4;
        widows: 4;
      }
      
      /* Protect divs with page-break styles */
      .markdown-body div[style*="page-break"] {
        page-break-inside: avoid !important;
        break-inside: avoid-page !important;
      }
      
      /* General protection but allow some breaks for long content */
      .markdown-body p, .markdown-body ul, .markdown-body ol {
        orphans: 3;
        widows: 3;
      }
    }
    
    @page { margin: 0.6in; }
  `;

  const getAssistantHtmlByIndex = (idx: number): string | null => {
    const el = assistantContentRefs.current[idx];
    if (!el) return null;
    return `<div class="markdown-body">${el.innerHTML}</div>`;
  };

  const handlePrintAssistant = (idx: number) => {
    const html = getAssistantHtmlByIndex(idx);
    if (!html) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Teaching Content</title><style>${printableCss}</style></head><body>${html}<script>window.onload = function(){window.print();}</script></body></html>`);
    win.document.close();
    win.focus();
  };

  const handleDownloadAssistantPDF = async (idx: number) => {
    const html = getAssistantHtmlByIndex(idx);
    if (!html) return;
    const container = document.createElement('div');
    container.innerHTML = `<style>${printableCss}</style>${html}`;
    const opt = {
      margin: 0.6,
      filename: 'teaching-content.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy', 'avoid-all'] },
    } as any;
    await html2pdf().set(opt).from(container).save();
  };

  const handleDownloadAssistantWord = (idx: number) => {
    const html = getAssistantHtmlByIndex(idx);
    if (!html) return;
    const full = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Teaching Content</title><style>${printableCss}</style></head><body>${html}</body></html>`;
    const blob = htmlDocx.asBlob(full);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'teaching-content.docx'; a.click();
    URL.revokeObjectURL(url);
  };

  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [downloadMenuForIdx, setDownloadMenuForIdx] = useState<number | null>(null);
  const openDownloadMenu = (evt: React.MouseEvent<HTMLElement>, idx: number) => { setDownloadMenuAnchor(evt.currentTarget); setDownloadMenuForIdx(idx); };
  const closeDownloadMenu = () => { setDownloadMenuAnchor(null); setDownloadMenuForIdx(null); };

  const scheduleSmoothScrollToBottom = useCallback(() => {
    if (scrollRaf.current != null) return;
    scrollRaf.current = requestAnimationFrame(() => {
      scrollRaf.current = null;
      const el = scrollContainerRef.current;
      if (!el) return;
      const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      const near = distanceToBottom < 120; // allow user to read if scrolled up
      setIsNearBottom(near);
      if (near) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (scrollRaf.current != null) cancelAnimationFrame(scrollRaf.current);
    };
  }, []);

  // Recompute bottom proximity when content changes (no auto-scroll)
  useEffect(() => {
    const el = scrollContainerRef.current; if (!el) return;
    const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    setIsNearBottom(distanceToBottom < 120);
  }, [currentStream, messages.length]);

  // Track user scroll to toggle the down-arrow affordance
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      setIsNearBottom(distanceToBottom < 120);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll as any);
  }, []);

  // Load conversations on mount
  useEffect(() => {
    if (teacher) refreshConversations();
  }, [teacher, refreshConversations]);

  // Default grade level and subject to teacher's profile on initial load (no active conversation)
  useEffect(() => {
    if (!teacher) return;
    if (activeConversationId) return; // don't override conversation settings
    
    // Set grade level if not already set
    if (!gradeLevel) {
      const raw = String(teacher.gradeLevel || '').trim();
      if (raw) {
        // Normalize values like '5', '5th', 'Grade 5', 'K'
        let normalized = '';
        const digits = raw.match(/\d+/);
        if (/^k/i.test(raw)) normalized = 'K';
        else if (digits) normalized = String(parseInt(digits[0], 10));
        // Check against available grade levels or common grade level values
        const validGradeLevels = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'Reading', 'Speech'];
        if (normalized && validGradeLevels.includes(normalized)) {
          setGradeLevel(normalized);
        }
      }
    }
    
    // Set subject from teacher's primary subject if not already set
    if (!subject && teacher.primarySubject) {
      setSubject(teacher.primarySubject);
    }
  }, [teacher, activeConversationId, gradeLevel, subject]);

  // Removed auto-create-on-mount to avoid saving empty conversations

  // Load messages whenever active conversation changes
  useEffect(() => {
    const load = async () => {
      // Skip loading if we're in the middle of creating this specific conversation
      // (the messages are already in state from streaming, don't overwrite them)
      // Using ID comparison instead of boolean flag prevents race conditions
      if (creatingConversationIdRef.current === activeConversationId) { return; }
      
      if (!activeConversationId) { setMessages([]); return; }
      try {
        const res = await apiAxiosInstance.get(`/api/teaching-chat/conversations/${activeConversationId}`);
        const convo = res.data?.conversation;
        const loadedMessages = (res.data?.messages || []).map((m: any) => ({ role: m.role, content: m.content }));
        setMessages(loadedMessages);
        setModeState(convo?.settings?.mode || 'guided');
        setGradeLevel(convo?.settings?.gradeLevel || '');
        setSubject(convo?.settings?.subject || 'ELA');
        setSelectedStandards(convo?.settings?.standardIds || []);
        
        // Scroll to bottom after loading an existing conversation
        if (loadedMessages.length > 0) {
          setTimeout(() => {
            const el = scrollContainerRef.current;
            if (el) {
              el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            }
          }, 100);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [activeConversationId]);

  // Use custom hook to get teacher's standard name
  const { standardName: teachingStandardName, loading: standardLoading, error: standardError } = useTeacherStandard(teacher);
  
  // Filter standards by teacher's teaching standard
  const filteredStandards = useFilterStandardsByTeaching(standards, teachingStandardName, 'TeachingChat');
  
  // Get available strands from filtered standards
  const availableStrands = useMemo(() => {
    const strandsSet = new Set<string>();
    filteredStandards.forEach((standard: any) => {
      if (standard.strand) {
        strandsSet.add(standard.strand);
      }
    });
    const strandsArray = Array.from(strandsSet).sort();
    
    // Capitalize first letter of each strand for display
    const capitalizedStrands = strandsArray.map((strand: string) => {
      return strand.charAt(0).toUpperCase() + strand.slice(1).toLowerCase();
    });
    
    // Find Reading (case-insensitive) and ensure it's first
    const readingIndex = capitalizedStrands.findIndex((s: string) => s.toLowerCase() === 'reading');
    if (readingIndex !== -1) {
      const reading = capitalizedStrands[readingIndex];
      return [reading, ...capitalizedStrands.filter((s: string) => s !== reading)];
    }
    return capitalizedStrands;
  }, [filteredStandards]);
  
  // Map of capitalized display names to original DB values
  const strandDisplayToOriginal = useMemo(() => {
    const map = new Map<string, string>();
    filteredStandards.forEach((standard: any) => {
      if (standard.strand) {
        const capitalized = standard.strand.charAt(0).toUpperCase() + standard.strand.slice(1).toLowerCase();
        if (!map.has(capitalized)) {
          map.set(capitalized, standard.strand);
        }
      }
    });
    return map;
  }, [filteredStandards]);
  
  // Filter standards by selected strand
  const strandFilteredStandards = useMemo(() => {
    if (!selectedStrand || selectedStrand === 'All') {
      return filteredStandards;
    }
    // Get original strand value from display name
    const originalStrand = strandDisplayToOriginal.get(selectedStrand) || selectedStrand;
    return filteredStandards.filter((standard: any) => standard.strand === originalStrand);
  }, [filteredStandards, selectedStrand, strandDisplayToOriginal]);
  
  // Update selectedStrand when availableStrands changes to ensure Reading is selected if available
  useEffect(() => {
    if (availableStrands.length > 0) {
      const readingStrand = availableStrands.find((s: string) => s.toLowerCase() === 'reading');
      // If current selection is not in available strands, or it's the default 'Reading' value
      if (!availableStrands.includes(selectedStrand)) {
        // Select Reading if available, otherwise "All Strands"
        setSelectedStrand(readingStrand || 'All');
      } else if (readingStrand && selectedStrand === 'Reading') {
        // If we have the default 'Reading' string but the actual value is different, update it
        if (selectedStrand !== readingStrand) {
          setSelectedStrand(readingStrand);
        }
      }
    }
  }, [availableStrands]); // Only depend on availableStrands to avoid infinite loop

  // Fetch available grade levels from database based on subject and teaching standards
  useEffect(() => {
    const fetchGradeLevels = async () => {
      if (!teacher?.teachingStandards || !Array.isArray(teacher.teachingStandards) || teacher.teachingStandards.length === 0) {
        setAvailableGradeLevels([]);
        return;
      }

      // Extract the first teaching standard ID (handle both string and object formats)
      const teachingStandardsId = extractTeachingStandardId(teacher.teachingStandards);

      if (!teachingStandardsId) {
        setAvailableGradeLevels([]);
        return;
      }

      setLoadingGradeLevels(true);
      try {
        const params: any = { 
          teachingStandards: teachingStandardsId
        };
        // Only add subject filter if subject is set
        if (subject) {
          params.subject = subject;
        }
        const res = await apiAxiosInstance.get('/api/detailedstandards/grade-levels', { params });
        setAvailableGradeLevels(res.data || []);
      } catch (e) {
        console.error('Error fetching grade levels:', e);
        setAvailableGradeLevels([]);
        setActionError('Could not load grade levels for your standards.');
      } finally {
        setLoadingGradeLevels(false);
      }
    };

    fetchGradeLevels();
  }, [subject, teacher?.teachingStandards]);

  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (str: string | undefined | null): string => {
    if (!str || str.length === 0) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Fetch standards for selection (filtered by grade, subject, and standards system)
  // Note: Using ref to track fetch state prevents dependency cycle issues
  const fetchStandardsRef = useRef<AbortController | null>(null);
  
  const fetchStandards = useCallback(async () => {
    // Cancel any in-flight request
    if (fetchStandardsRef.current) {
      fetchStandardsRef.current.abort();
    }
    
    setLoadingStandards(true);
    try {
      if (!gradeLevel) {
        setStandards([]);
        setStandardsHasMore(false);
        return;
      }
      
      // Create new abort controller for this request
      fetchStandardsRef.current = new AbortController();
      
      const params: any = { gradeLevel, subject };
      const res = await apiAxiosInstance.get('/api/detailedstandards', { 
        params,
        signal: fetchStandardsRef.current.signal 
      });
      const list = res.data || [];
      
      setStandards(list);
      setStandardsHasMore(false);
    } catch (e: any) { 
      // Ignore abort errors
      if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
        console.error('Error loading standards:', e);
        setActionError('Could not load standards. Please try again.');
      }
    } finally { 
      setLoadingStandards(false); 
    }
  }, [gradeLevel, subject]); // Only depend on values actually used

  useEffect(() => {
    // When grade, subject, teachingStandards, or teachingStandardName changes, clear selected standards and refetch
    setSelectedStandards([]);
    setStandardsSkip(0);
    setStandardsHasMore(true);
    fetchStandards();
    
    // Cleanup: abort request if component unmounts or dependencies change
    return () => {
      if (fetchStandardsRef.current) {
        fetchStandardsRef.current.abort();
      }
    };
  }, [gradeLevel, subject, teacher?.teachingStandards, teachingStandardName, fetchStandards]);

  const createConversation = async (): Promise<Conversation> => {
    const res = await apiAxiosInstance.post('/api/teaching-chat/conversations', {
      title: 'Untitled conversation',
      settings: { mode, gradeLevel, subject, standardIds: selectedStandards }
    });
    const created: Conversation = res.data;
    setConversations((prev) => [created, ...prev]);
    setActiveConversationId(created._id);
    // Make sure sidebar picks it up with any server-side defaults
    refreshConversations();
    return created;
  };

  const renameConversation = async (id: string) => {
    const current = conversations.find(c => c._id === id);
    const newTitle = window.prompt('Rename conversation', current?.title || '');
    if (!newTitle || !newTitle.trim()) return;
    try {
      const res = await apiAxiosInstance.patch(`/api/teaching-chat/conversations/${id}`, { title: newTitle.trim() });
      setConversations(prev => prev.map(c => (c._id === id ? { ...c, title: res.data.title } : c)));
    } catch (e) {
      console.error(e);
      setActionError('Could not rename the conversation.');
    }
  };

  const deleteConversation = async (id: string) => {
    await apiAxiosInstance.delete(`/api/teaching-chat/conversations/${id}`);
    setConversations((prev) => prev.filter(c => c._id !== id));
    if (activeConversationId === id) {
      creatingConversationIdRef.current = null; // Clear ref when active conversation is deleted
      setActiveConversationId(conversations.find(c => c._id !== id)?._id || null);
      setMessages([]);
    }
    refreshConversations();
  };

  // Handler for when user explicitly clicks on a conversation in the sidebar
  // Clears the creation ref to ensure the conversation is loaded from server
  const selectConversation = useCallback((id: string | null) => {
    creatingConversationIdRef.current = null;
    setActiveConversationId(id);
    if (id === null) {
      setMessages([]);
      setInput('');
    }
  }, []);

  const updateSettings = async (newSettings: { mode?: 'guided' | 'free'; gradeLevel?: string; subject?: string; standardIds?: string[] }) => {
    setModeState(newSettings.mode ?? mode);
    setGradeLevel(newSettings.gradeLevel ?? gradeLevel);
    setSubject(newSettings.subject ?? subject);
    setSelectedStandards(newSettings.standardIds ?? selectedStandards);
    if (!activeConversationId) return;
    try {
      await apiAxiosInstance.patch(`/api/teaching-chat/conversations/${activeConversationId}`, {
        settings: {
          mode: newSettings.mode ?? mode,
          gradeLevel: newSettings.gradeLevel ?? gradeLevel,
          subject: newSettings.subject ?? subject,
          standardIds: newSettings.standardIds ?? selectedStandards,
        }
      });
    } catch (e) { console.error(e); }
  };

  const setMode = useCallback((newMode: 'guided' | 'free') => {
    updateSettings({ mode: newMode });
  }, [updateSettings]);

  const selectedStandardCodes = useMemo(() => {
    const map = new Map(filteredStandards.map(s => [s._id, s.code || s.standard]));
    return selectedStandards
      .map(id => (map.get(id) || id))
      .filter(Boolean) as string[];
  }, [selectedStandards, filteredStandards]);

  const suggestedPrompts = useMemo(() => {
    const prompts: string[] = [];
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    const topic = lastUser?.content?.slice(0, 100) || '';
    const gradeText = gradeLevel ? `for Grade ${gradeLevel}` : '';
    const stds = selectedStandardCodes.length ? ` aligned to ${selectedStandardCodes.join(', ')}` : '';

    // Heuristics on the last assistant message to tailor suggestions
    const a = (lastAssistant?.content || '').toLowerCase();
    const showsLessonPlan = /lesson plan|objectives|materials|procedures|activities|assessment/.test(a);
    const showsQuestions = /questions?:|question set|multiple choice|short answer|text-dependent/.test(a);
    const showsRubric = /rubric|criteria|scoring/.test(a);
    const showsProblems = /problems?:|practice|worked example|word problems?/.test(a);
    const showsCER = /claim[- ]?evidence[- ]?reasoning|cer/.test(a);

    // General next steps based on detected structure
    if (showsLessonPlan) {
      prompts.push(`Create formative assessment checks ${gradeText}${stds}`.trim());
      prompts.push(`Provide differentiation (EL/504/IEP) notes ${gradeText}${stds}`.trim());
      prompts.push(`Turn this lesson into a printable handout ${gradeText}${stds}`.trim());
      prompts.push(`Add exit ticket questions ${gradeText}${stds}`.trim());
    }
    if (showsQuestions) {
      prompts.push(`Provide answer key with rationales ${gradeText}${stds}`.trim());
      prompts.push(`Increase rigor and add 3 more questions ${gradeText}${stds}`.trim());
      prompts.push(`Convert to Google Form friendly format ${gradeText}${stds}`.trim());
    }
    if (showsRubric) {
      prompts.push(`Generate student-facing checklist ${gradeText}${stds}`.trim());
      prompts.push(`Create exemplars at 4 performance levels ${gradeText}${stds}`.trim());
    }
    if (showsProblems) {
      prompts.push(`Create a worksheet with 12 problems and answer key ${gradeText}${stds}`.trim());
      prompts.push(`Add real‑life context problems ${gradeText}${stds}`.trim());
    }
    if (showsCER) {
      prompts.push(`Draft a CER prompt and scoring guide ${gradeText}${stds}`.trim());
    }

    // Subject-specific baselines if nothing detected
    if (prompts.length === 0) {
      if (subject === 'Math') {
        prompts.push(`8 scaffolded practice problems ${gradeText}${stds}`.trim());
        prompts.push(`Common misconceptions and fixes ${gradeText}${stds}`.trim());
      } else if (subject === 'ELA') {
        prompts.push(`Text-dependent questions ${gradeText}${stds}`.trim());
        prompts.push(`Graphic organizer suggestions ${gradeText}${stds}`.trim());
      } else if (subject === 'Science') {
        prompts.push(`Inquiry lab outline with safety ${gradeText}${stds}`.trim());
        prompts.push(`CER prompt ${gradeText}${stds}`.trim());
      } else if (subject === 'Social Studies') {
        prompts.push(`Primary source analysis questions ${gradeText}${stds}`.trim());
        prompts.push(`DBQ outline ${gradeText}${stds}`.trim());
      }
    }

    // Contextual follow-ups referencing the latest user prompt
    if (topic) {
      prompts.push(`Differentiate for EL students: ${topic}`);
      prompts.push(`Create a 10‑minute warm‑up for: ${topic}${stds}`.trim());
      prompts.push(`Exit ticket about: ${topic}${stds}`.trim());
    }

    // De-duplicate and limit
    return Array.from(new Set(prompts)).slice(0, 8);
  }, [messages, subject, gradeLevel, selectedStandardCodes]);

  // Build standard descriptions array for AI (code + description format)
  const selectedStandardDescriptions = useMemo(() => {
    if (selectedStandards.length === 0 || filteredStandards.length === 0) return [];
    const standardMap = new Map(filteredStandards.map(s => [s._id, s]));
    const descriptions: string[] = [];
    
    selectedStandards.forEach(id => {
      const standard = standardMap.get(id);
      if (standard) {
        const code = standard.code || standard.standard || '';
        const desc = standard.description || standard.short_description || '';
        if (code && desc) {
          // Format: "5.13(A) - Generate and clarify questions on a topic for formal and informal inquiry"
          descriptions.push(`${code} - ${desc}`);
        } else if (code) {
          descriptions.push(code);
        }
      }
    });
    
    return descriptions;
  }, [selectedStandards, filteredStandards]);

  // In Open Chat mode (mode: 'free'), no grade/subject/standards context should reach the AI or be persisted,
  // even though the raw selections are kept in state so switching back to Guided restores them.
  const isGuided = mode === 'guided';
  const effectiveGradeLevel = isGuided ? gradeLevel : '';
  const effectiveSubject = isGuided ? subject : '';
  const effectiveStandardIds = isGuided ? selectedStandards : [];
  const effectiveStandardCodes = isGuided ? selectedStandardCodes : [];
  const effectiveStandardDescriptions = isGuided ? selectedStandardDescriptions : [];

  const [aiSuggested, setAiSuggested] = useState<string[]>([]);
  const [aiSuggestLoading, setAiSuggestLoading] = useState<boolean>(false);
  const [showMoreContextual, setShowMoreContextual] = useState<boolean>(false);

  // Compute personalization text for suggestions
  const personalizationText = useMemo(() => {
    return getPersonalizationText(
      gradeLevel,
      subject,
      selectedStandardCodes,
      teacher?.subjectPreferences?.standardsSystem
    );
  }, [gradeLevel, subject, selectedStandardCodes, teacher?.subjectPreferences?.standardsSystem]);

  // Compute prioritized contextual suggestions (combining AI suggested and local heuristic-based)
  const prioritizedContextualSuggestions = useMemo(() => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    const lastAssistantContent = lastAssistant?.content || '';
    
    // Combine AI suggestions with local suggestedPrompts
    const allSuggestions = [...aiSuggested, ...suggestedPrompts];
    
    // Deduplicate
    const uniqueSuggestions = Array.from(new Set(allSuggestions));
    
    // Score and sort by priority
    const scored = uniqueSuggestions.map(s => ({
      label: s,
      priority: getSuggestionPriority(s, lastAssistantContent),
      icon: getSuggestionIcon(s),
    }));
    
    // Sort by priority (highest first)
    scored.sort((a, b) => b.priority - a.priority);
    
    return scored;
  }, [aiSuggested, suggestedPrompts, messages]);

  // Split into visible and hidden suggestions
  const INITIAL_VISIBLE_COUNT = 4;
  const visibleContextualSuggestions = prioritizedContextualSuggestions.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenContextualSuggestions = prioritizedContextualSuggestions.slice(INITIAL_VISIBLE_COUNT);
  const hasMoreContextual = hiddenContextualSuggestions.length > 0;

  useEffect(() => {
    // Suggestions are only surfaced in the Guided-mode Teaching Panel, so skip the call in Open Chat mode.
    if (!isGuided) return;
    // Fetch AI-suggested prompts after each assistant message
    const lastRole = messages[messages.length - 1]?.role;
    if (!lastRole || lastRole !== 'assistant') return;
    (async () => {
      try {
        setAiSuggestLoading(true);
        setAiSuggested([]);
        const res = await aiAxiosInstance.post('/suggest-prompts', {
          messages,
          gradeLevel: effectiveGradeLevel,
          subject: effectiveSubject,
          standardCodes: effectiveStandardCodes,
          standardDescriptions: effectiveStandardDescriptions,
          maxSuggestions: 6,
        });
        if (Array.isArray(res.data?.suggested)) setAiSuggested(res.data.suggested);
      } catch (e) {
        // ignore
      } finally { setAiSuggestLoading(false); }
    })();
  }, [messages, isGuided, effectiveGradeLevel, effectiveSubject, effectiveStandardCodes, effectiveStandardDescriptions]);

  const composedContext = useMemo(() => {
    const parts: string[] = [];
    if (effectiveGradeLevel) parts.push(`Grade Level: ${effectiveGradeLevel}`);
    if (effectiveSubject) parts.push(`Subject: ${effectiveSubject}`);

    // Include standard codes AND descriptions for accurate lesson generation
    if (effectiveStandardDescriptions.length > 0) {
      parts.push(`Learning Standards:\n${effectiveStandardDescriptions.join('\n')}`);
    } else if (effectiveStandardCodes.length) {
      // Fallback if no descriptions available
      parts.push(`Standards: ${effectiveStandardCodes.join(', ')}`);
    }

    parts.push('Produce Markdown. Structure logically with clear headings, lists, and classroom-ready steps.');
    parts.push('Maintain educational focus and encourage clear reasoning.');
    return parts.join('\n');
  }, [effectiveGradeLevel, effectiveSubject, effectiveStandardDescriptions, effectiveStandardCodes]);

  // Helper function to format grade level properly (e.g., "5" -> "5th grade", "K" -> "K")
  const formatGradeLevel = (grade: string): string => {
    if (!grade) return '';
    const trimmed = grade.trim();
    
    // Handle special cases
    if (/^k$/i.test(trimmed)) return 'K';
    if (/^pre.?k/i.test(trimmed)) return 'Pre-K';
    
    // Extract numeric grade
    const numMatch = trimmed.match(/\d+/);
    if (!numMatch) return trimmed; // Return as-is if no number
    
    const num = parseInt(numMatch[0], 10);
    if (isNaN(num)) return trimmed;
    
    // Convert to ordinal (1st, 2nd, 3rd, 4th, etc.)
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;
    let suffix = 'th';
    if (lastTwoDigits < 10 || lastTwoDigits > 20) {
      if (lastDigit === 1) suffix = 'st';
      else if (lastDigit === 2) suffix = 'nd';
      else if (lastDigit === 3) suffix = 'rd';
    }
    
    return `${num}${suffix} grade`;
  };

  // Smart title generation: Extract key topic/info first, then add context (grade, subject)
  // Pass availableStandards explicitly instead of relying on closure to avoid stale data
  const generateSmartTitle = (
    aiResponse: string, 
    userMessage: string, 
    grade: string, 
    subj: string, 
    standardIds: string[] = [],
    availableStandards: Array<{ _id: string; description?: string; short_description?: string; code?: string; standard?: string; strand?: string }> = []
  ): string => {
    // Priority 1: Try to get standard description(s) - this is the most meaningful identifier
    let topic = '';
    if (standardIds.length > 0 && availableStandards.length > 0) {
      const standardMap = new Map(availableStandards.map(s => [s._id, s]));
      const selectedStandardsData = standardIds
        .map(id => standardMap.get(id))
        .filter(Boolean) as Array<{ description?: string; short_description?: string; code?: string }>;
      
      if (selectedStandardsData.length > 0) {
        // Use the first standard's description or short_description
        const firstStandard = selectedStandardsData[0];
        const standardDesc = firstStandard.description || firstStandard.short_description || '';
        if (standardDesc) {
          // Clean up the description - take first meaningful part (before any colons or long explanations)
          topic = standardDesc
            .split(/[:;]/)[0] // Take part before first colon or semicolon
            .split('.')[0] // Take first sentence
            .trim();
        }
      }
    }
    
    // Priority 2: Extract from heading, removing generic prefixes
    if (!topic) {
      const headingMatch = /^\s*#+\s+(.+)$/m.exec(aiResponse);
      if (headingMatch) {
        let heading = headingMatch[1].trim();
        // Remove common generic prefixes to get to the key info
        heading = heading
          .replace(/^(Grade\s+\d+\s+)?Lesson\s+Plan:?\s*/i, '')
          .replace(/^(Grade\s+\d+\s+)?Lesson:?\s*/i, '')
          .replace(/^(Grade\s+\d+\s+)?(ELA|Math|Science|Social Studies)\s+Lesson:?\s*/i, '')
          .replace(/^Teaching\s+Plan:?\s*/i, '')
          .replace(/^(Reading|Writing|Mathematics)\s+Lesson:?\s*/i, '')
          .trim();
        if (heading) topic = heading;
      }
    }
    
    // Priority 3: Extract from user message - preserve complete phrases
    if (!topic) {
      // Clean up user message but preserve meaningful context
      let cleaned = userMessage
        .replace(/^(create|generate|make|write|help me|can you|please|i need|i want)\s+/i, '')
        .replace(/\s+(lesson plan|lesson|activity|worksheet|for me|for my students?|please)$/i, '')
        .trim();
      
      // Ensure we have a complete phrase - don't cut off mid-sentence
      if (cleaned && cleaned.length > 5) {
        // Check if it ends with incomplete phrases like "within a" or "for a"
        const incompletePhrases = /\b(within|for|about|on|in|with)\s+(a|an|the)?\s*$/i;
        if (incompletePhrases.test(cleaned)) {
          // Try to complete the phrase from context or remove the incomplete part
          cleaned = cleaned.replace(incompletePhrases, '').trim();
        }
        topic = cleaned;
      }
    }
    
    // Priority 4: Fallback to first meaningful words from AI response
    if (!topic) {
      const plain = aiResponse.replace(/[#*_`>-]+/g, ' ').replace(/\s+/g, ' ').trim();
      const words = plain.split(' ').filter(w => w.length > 0);
      // Skip common introductory words
      const meaningfulWords = words.filter(w => 
        !['the', 'a', 'an', 'this', 'that', 'here', 'is', 'are', 'for', 'to', 'in', 'on'].includes(w.toLowerCase())
      );
      topic = meaningfulWords.slice(0, 8).join(' '); // Increased from 6 to 8 for more context
    }
    
    // Ensure topic is complete - don't cut off mid-word or mid-phrase
    // Limit topic length but preserve complete words and phrases
    if (topic.length > 60) {
      const words = topic.split(' ');
      topic = '';
      for (const word of words) {
        const testLength = (topic + ' ' + word).trim().length;
        // Stop before exceeding limit, but ensure we have at least 3 words
        if (testLength > 60 && topic.split(' ').length >= 3) break;
        topic = (topic + ' ' + word).trim();
      }
    }
    
    // Build title: topic first, then formatted grade and "lesson plan"
    let title = topic || 'Lesson';
    
    // Format grade level properly (e.g., "5" -> "5th grade")
    const formattedGrade = formatGradeLevel(grade);
    
    // Add grade and "lesson plan" at the end (if not already in topic)
    const isLessonPlan = /lesson plan|objectives|materials|procedures|activities|assessment/i.test(aiResponse) ||
                        /lesson plan/i.test(userMessage);
    
    // Check if grade is already mentioned in topic (various formats)
    const gradeInTopic = formattedGrade && (
      topic.toLowerCase().includes(formattedGrade.toLowerCase()) ||
      topic.toLowerCase().includes(grade.toLowerCase()) ||
      /\b\d+\s*(grade|th|st|nd|rd)\b/i.test(topic)
    );
    
    // Check if "lesson plan" is already in topic
    const lessonPlanInTopic = /lesson plan/i.test(topic);
    
    // Build suffix
    const suffixParts: string[] = [];
    if (formattedGrade && !gradeInTopic) {
      suffixParts.push(formattedGrade);
    }
    
    if (isLessonPlan && !lessonPlanInTopic) {
      if (suffixParts.length > 0) {
        title = `${title} ${suffixParts.join(' ')} lesson plan`;
      } else {
        title = `${title} lesson plan`;
      }
    } else if (suffixParts.length > 0) {
      title = `${title} ${suffixParts.join(' ')}`;
    }
    
    // Final cleanup - ensure complete sentences/phrases
    title = title
      .replace(/[:]/g, ' -')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Remove trailing incomplete phrases
    title = title.replace(/\s+(within|for|about|on|in|with)\s+(a|an|the)?\s*$/i, '').trim();
    
    // Ensure we don't exceed a reasonable length, but preserve complete words
    if (title.length > 100) {
      const words = title.split(' ');
      title = '';
      for (const word of words) {
        const testLength = (title + ' ' + word).trim().length;
        if (testLength > 100) break;
        title = (title + ' ' + word).trim();
      }
    }
    
    return title || 'Lesson';
  };

  const sendMessage = async () => {
    if ((!input.trim() && composeAttachments.length === 0) || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: userMessage, attachments: composeAttachments }]);
    setCurrentStream('');
    // Enable one-time auto-scroll for this new assistant response
    autoScrollOncePending.current = true;

    // Track if this is a new conversation (conversation will be created after AI response)
    let convoId = activeConversationId;
    const isNewConversation = !convoId;

    try {
      // Only persist user message if conversation already exists
      if (convoId) {
        apiAxiosInstance
          .post(`/api/teaching-chat/conversations/${convoId}/messages`, { role: 'user', content: userMessage, attachments: composeAttachments })
          .catch(() => {});
      }

      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      let assistantFull = '';
      let streamMeta: { model?: string; latency_ms?: number; contentType?: string; showButtons?: boolean } = {};

      await consumeSSEStream({
        url: `${aiAxiosInstance.defaults.baseURL}/chat`,
        headers: buildAiAuthHeaders(),
        body: JSON.stringify({
          message: userMessage,
          messages: [...messages, { role: 'user', content: userMessage, attachments: composeAttachments }],
          context: composedContext,
          mode,
          gradeLevel: effectiveGradeLevel,
          subject: effectiveSubject,
          standardIds: effectiveStandardIds,
          standardCodes: effectiveStandardCodes,
          standardDescriptions: effectiveStandardDescriptions,
        }),
        signal: abortRef.current.signal,
        onEvent: (data) => {
          if (data?.event === 'done') {
            streamMeta = {
              model: data.model as string | undefined,
              latency_ms: data.latency_ms as number | undefined,
              contentType: data.contentType as string | undefined,
              showButtons: data.showButtons as boolean | undefined,
            };
            return;
          }
          if (typeof data.content === 'string') {
            assistantFull += data.content;
            setCurrentStream((prev) => prev + data.content);
            if (autoScrollOncePending.current) {
              requestAnimationFrame(() => {
                const el = scrollContainerRef.current;
                if (!el) return;
                const targetTop = Math.min(
                  el.scrollTop + el.clientHeight,
                  el.scrollHeight - el.clientHeight,
                );
                el.scrollTo({ top: targetTop, behavior: 'smooth' });
              });
              autoScrollOncePending.current = false;
            }
          }
        },
      });

      // Add assistant message after the user message (which was added at line ~715)
      // The order is already correct: user message added first, then assistant message
      setMessages(prev => [...prev, { role: 'assistant', content: assistantFull, meta: streamMeta }]);
      setCurrentStream('');
      setComposeAttachments([]);

      // Handle conversation creation/update based on whether this is a new conversation
      if (isNewConversation) {
        // For NEW conversations: Generate title using AI endpoint
        let generatedTitle = 'Lesson';
        try {
          // Get standard descriptions if available
          const standardDescriptions: string[] = [];
          if (effectiveStandardIds.length > 0 && filteredStandards.length > 0) {
            const standardMap = new Map(filteredStandards.map(s => [s._id, s]));
            effectiveStandardIds.forEach(id => {
              const standard = standardMap.get(id);
              if (standard?.description) {
                standardDescriptions.push(standard.description);
              } else if (standard?.short_description) {
                standardDescriptions.push(standard.short_description);
              }
            });
          }

          const titleResponse = await aiAxiosInstance.post('/generate-title', {
            userMessage,
            aiResponse: assistantFull,
            gradeLevel: effectiveGradeLevel,
            subject: effectiveSubject,
            standardCodes: effectiveStandardCodes,
            standardDescriptions: standardDescriptions.length > 0 ? standardDescriptions : undefined
          });

          generatedTitle = titleResponse.data?.title || generateSmartTitle(
            assistantFull,
            userMessage,
            effectiveGradeLevel,
            effectiveSubject,
            effectiveStandardIds,
            filteredStandards
          );
        } catch (e) {
          console.error('Failed to generate title with AI, using fallback:', e);
          // Fallback to client-side generation if AI fails
          generatedTitle = generateSmartTitle(
            assistantFull,
            userMessage,
            effectiveGradeLevel,
            effectiveSubject,
            effectiveStandardIds,
            filteredStandards
          );
        }

        // Create conversation with the generated title
        try {
          const res = await apiAxiosInstance.post('/api/teaching-chat/conversations', {
            title: generatedTitle,
            settings: { mode, gradeLevel: effectiveGradeLevel, subject: effectiveSubject, standardIds: effectiveStandardIds }
          });
          const created = res.data;
          convoId = created._id;
          setConversations((prev) => [created, ...prev]);
          
          // Track this conversation ID to prevent the useEffect from reloading messages
          // Using ID comparison is more robust than a boolean flag against race conditions
          creatingConversationIdRef.current = created._id;
          setActiveConversationId(created._id);

          // Save messages sequentially to ensure correct order (user first, then assistant)
          // Use sequential await instead of Promise.all to guarantee order in database
          try {
            // Save user message first - MUST succeed before saving assistant message
            await apiAxiosInstance.post(`/api/teaching-chat/conversations/${convoId}/messages`, { 
              role: 'user', 
              content: userMessage, 
              attachments: composeAttachments 
            });
            // Then save assistant message - only if user message succeeded
            await apiAxiosInstance.post(`/api/teaching-chat/conversations/${convoId}/messages`, { 
              role: 'assistant', 
              content: assistantFull 
            });
          } catch (e) {
            console.error('Failed to save messages:', e);
            // If message persistence fails, conversation exists but messages are lost
            // User can retry by sending a new message
          }
          // Note: We intentionally don't clear creatingConversationIdRef here
          // It will be cleared when user switches to a different conversation or sends another message

          // No need to refresh - we already have the conversation in state and messages are preserved
        } catch (e) {
          console.error('Failed to create conversation:', e);
        }
      } else {
        // For EXISTING conversations: Persist assistant message
        if (convoId) {
          apiAxiosInstance
            .post(`/api/teaching-chat/conversations/${convoId}/messages`, { role: 'assistant', content: assistantFull })
            .catch(() => {});
        }

        // Update title if it's still "Untitled"
        const currentConvo = conversations.find(c => c._id === convoId);
        if (currentConvo && (!currentConvo.title || currentConvo.title.startsWith('Untitled'))) {
          let suggested = 'Lesson';
          try {
            // Get standard descriptions if available
            const standardDescriptions: string[] = [];
            if (effectiveStandardIds.length > 0 && filteredStandards.length > 0) {
              const standardMap = new Map(filteredStandards.map(s => [s._id, s]));
              effectiveStandardIds.forEach(id => {
                const standard = standardMap.get(id);
                if (standard?.description) {
                  standardDescriptions.push(standard.description);
                } else if (standard?.short_description) {
                  standardDescriptions.push(standard.short_description);
                }
              });
            }

            const titleResponse = await aiAxiosInstance.post('/generate-title', {
              userMessage,
              aiResponse: assistantFull,
              gradeLevel: effectiveGradeLevel,
              subject: effectiveSubject,
              standardCodes: effectiveStandardCodes,
              standardDescriptions: standardDescriptions.length > 0 ? standardDescriptions : undefined
            });

            suggested = titleResponse.data?.title || generateSmartTitle(
              assistantFull,
              userMessage,
              effectiveGradeLevel,
              effectiveSubject,
              effectiveStandardIds,
              filteredStandards
            );
          } catch (e) {
            console.error('Failed to generate title with AI, using fallback:', e);
            // Fallback to client-side generation if AI fails
            suggested = generateSmartTitle(
              assistantFull,
              userMessage,
              effectiveGradeLevel,
              effectiveSubject,
              effectiveStandardIds,
              filteredStandards
            );
          }
          
          try {
            const res = await apiAxiosInstance.patch(`/api/teaching-chat/conversations/${convoId}`, { title: suggested });
            setConversations(prev => prev.map(c => c._id === convoId ? { ...c, title: res.data.title } : c));
          } catch (e) { /* ignore title errors */ }
        }
      }
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I ran into an issue. Please try again.' }]);
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  };

  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLDivElement>) => {
    try {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items as any) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            await handleFiles([file]);
            return;
          }
        }
        if (item.type && item.type.startsWith('image/')) {
          const file = item.getAsFile?.();
          if (file) {
            e.preventDefault();
            await handleFiles([file]);
            return;
          }
        }
      }
    } catch {}
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFiles(files);
    }
  }, []);

  const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const formatFileSize = (bytes?: number): string => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || (files as any).length === 0) return;
    
    const fileArray = Array.from(files);
    const newAttachments: Attachment[] = [];
    
    for (const file of fileArray) {
      const name = (file.name || '').toLowerCase();
      try {
        if (name.endsWith('.txt')) {
          // For text files, extract text content
          const text = await file.text();
          const attachment: Attachment = {
            id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            type: 'document',
            filename: file.name || 'text-file',
            fileType: file.type || 'text/plain',
            fileSize: file.size,
            data: text, // Store text content
          };
          newAttachments.push(attachment);
        } else if (name.endsWith('.pdf')) {
          // For PDF files, extract text using PDF.js
          const arrayBuffer = await file.arrayBuffer();
          const pdfjs = await ensurePdfJsLoaded();
          const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
          let fullText = '';
          
          // Extract text from all pages
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item: any) => item.str).join(' ');
            fullText += pageText + '\n\n';
          }
          
          const attachment: Attachment = {
            id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            type: 'document',
            filename: file.name || 'pdf-document',
            fileType: file.type || 'application/pdf',
            fileSize: file.size,
            data: fullText.trim(), // Store extracted text content
          };
          newAttachments.push(attachment);
        } else if (name.endsWith('.docx')) {
          // For Word files, extract text using mammoth
          const arrayBuffer = await file.arrayBuffer();
          const mammoth = await ensureMammothLoaded();
          const result = await mammoth.extractRawText({ arrayBuffer });
          const text = (result && result.value) ? result.value : '';
          const attachment: Attachment = {
            id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            type: 'document',
            filename: file.name || 'word-document',
            fileType: file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            fileSize: file.size,
            data: text, // Store extracted text content
          };
          newAttachments.push(attachment);
        } else if (file.type && file.type.startsWith('image/')) {
          // For images, create attachment with base64 data for AI vision analysis
          const dataUrl = await fileToDataUrl(file);
          const attachment: Attachment = {
            id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            type: 'image',
            filename: file.name || 'pasted-image',
            fileType: file.type || 'image/*',
            fileSize: file.size,
            data: dataUrl,
          };
          newAttachments.push(attachment);
        } else {
          // For other file types, create a generic attachment
          const attachment: Attachment = {
            id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            type: 'document',
            filename: file.name || 'file',
            fileType: file.type || 'application/octet-stream',
            fileSize: file.size,
            data: `File: ${file.name} (${formatFileSize(file.size)})`, // Store file info
          };
          newAttachments.push(attachment);
        }
      } catch (err) {
        console.error('Failed to process file:', file.name, err);
        // Still create an attachment for failed files
        const attachment: Attachment = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
          type: 'document',
          filename: file.name || 'file',
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
          data: `Error processing file: ${file.name}`,
        };
        newAttachments.push(attachment);
      }
    }
    
    // Add all new attachments
    if (newAttachments.length > 0) {
      setComposeAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  // Dynamically load mammoth browser build if not present
  const ensureMammothLoaded = async (): Promise<any> => {
    const existing = (window as any).mammoth;
    if (existing) return existing;
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/mammoth@1.6.0/mammoth.browser.min.js';
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load mammoth'));
      document.head.appendChild(script);
    });
    return (window as any).mammoth;
  };

  // Dynamically load PDF.js if not present
  const ensurePdfJsLoaded = async (): Promise<any> => {
    const existing = (window as any).pdfjsLib;
    if (existing) return existing;
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js';
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        // Set the worker source
        const pdfjsLib = (window as any).pdfjsLib;
        if (pdfjsLib) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
        }
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load PDF.js'));
      document.head.appendChild(script);
    });
    return (window as any).pdfjsLib;
  };

  const stopGenerating = useCallback(() => {
    abortRef.current?.abort();
    setIsLoading(false);
  }, []);

  return {
    UNIVERSAL_TOOLS,
    subjects,
    abortRef,
    actionError,
    setActionError,
    activeConversationId,
    aiSuggestLoading,
    archiveConversation,
    assistantContentRefs,
    availableGradeLevels,
    availableStrands,
    capitalizeFirstLetter,
    closeConvMenu,
    closeDownloadMenu,
    composeAttachments,
    setComposeAttachments,
    convQuery,
    setConvQuery,
    conversations,
    currentStream,
    deleteConversation,
    downloadMenuAnchor,
    downloadMenuForIdx,
    fileInputRef,
    formatFileSize,
    gradeLevel,
    handleDownloadAssistantPDF,
    handleDownloadAssistantWord,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFiles,
    handlePaste,
    handlePrintAssistant,
    hasMoreContextual,
    hiddenContextualSuggestions,
    input,
    setInput,
    isDragOver,
    isLoading,
    isNearBottom,
    loadingGradeLevels,
    loadingStandards,
    mode,
    setMode,
    menuAnchorEl,
    menuConvId,
    messages,
    messagesEndRef,
    openConvMenu,
    openDownloadMenu,
    personalizationText,
    preview,
    setPreview,
    prioritizedContextualSuggestions,
    renameConversation,
    scrollContainerRef,
    selectConversation,
    selectedStandards,
    setSelectedStandards,
    selectedStrand,
    setSelectedStrand,
    sendMessage,
    setAiSuggested,
    setShowMoreContextual,
    showMoreContextual,
    strandFilteredStandards,
    standardLoading,
    stopGenerating,
    subject,
    updateSettings,
    visibleContextualSuggestions,
  };
}
